/* NetworkDiscoveryHistoryViewComponent.js */

"use strict";
const _ = require("lodash");

const BaseActivityViewComponent = require("../../../../lib/activity/BaseActivityViewComponent");
const DateColumnRenderer = require("./column_renderers/date/DateComponent");
const DurationColumnRenderer = require("./column_renderers/duration/DurationComponent");
const LinksColumnRenderer = require("./column_renderers/links/LinksComponent");

const Renderer = require("./NetworkDiscoveryHistoryViewRenderer");
const uString = require("underscore.string");

const PagingActivityFeed = require("./PagingHistoryFeed");

_.mixin(uString.exports());

class NetworkDiscoveryHistoryViewComponent extends BaseActivityViewComponent {
    constructor(
        logger,
        NetworkDiscoveryExecutionEntityDef,
        gameEditor,
        viewState,
        io,
        utils,
        userSession,
        solutionsManager,
        storageFactory,
        MUF
    ) {
        super(logger, gameEditor, io, utils, userSession, solutionsManager, storageFactory);

        this._viewState = viewState || {};
        this._networkDiscoveryExecutionEntityDef = NetworkDiscoveryExecutionEntityDef;
        this._columnConfig = {
            id: {
                label: "Execution Id",
                sortable: false,
                resizable: false,
                priority: 1,
                shown: false,
            },
            executedOn: {
                label: "Start Time",
                columnRendererComponent: DateColumnRenderer,
                width: 160,
                priority: 2,
            },
            completedOn: {
                label: "Duration",
                context: {},
                entityColumn: true,
                columnRendererComponent: DurationColumnRenderer,
                width: 160,
                priority: 3,
            },
            executedBy: {
                label: "Executed By",
                priority: 4,
            },
            status: {
                label: "Status",
                priority: 5,
                // columnRendererComponent: AuditStatusColumn,
            },
            discoverySuccessCount: {
                label: "Discovered",
                width: 150,
                priority: 6,
            },
            discoveryErrorCount: {
                label: "Error",
                width: 150,
                priority: 7,
            },
            discoveryDuplicateCount: {
                label: "Duplicate",
                width: 150,
                priority: 8,
            },
            deviceAddCount: {
                label: "Added",
                width: 150,
                priority: 9,
            },
            deviceExceedLicenseCount: {
                label: "License Exceeded",
                width: 150,
                priority: 10,
            },
            discoveryExcludedCount: {
                label: "Excluded",
                width: 150,
                priority: 11,
            },
            links: {
                label: "Logs",
                columnRendererComponent: LinksColumnRenderer,
                context: {
                    gameEditor: this._gameEditor,
                    triggerType: this._entity,
                    logger: this._logger,
                    modelUserFeedback: MUF,
                    parentContext: this,
                },
                entityColumn: true,
                sortable: false,
                priority: 11,
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _patchSorting(sorting) {
        let patchedSorting = _.clone(sorting);
        return patchedSorting;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get viewState() {
        return this.getViewState();
    }

    getViewState() {
        return this._viewState;
    }

    get columnConfig() {
        return this._columnConfig;
    }

    shutdown() {
        let promises = [];
        if (this._activityFeed && this._activityFeed.shutdown) {
            promises.push(this._entity.stopRelatedExecutionsFeed());
        } else {
            promises.push(Promise.resolve());
        }

        return Promise.all(promises)
            .then(() => {
                return super.shutdown();
            })
            .then(
                () => {
                    this._renderer = undefined;
                    this._networkDiscoveryExecutionEntityDef = undefined;
                    this._columnConfig = undefined;
                    this._viewState = undefined;
                    this._activityFeed = undefined;
                },
                (err) => {
                    this._logger.warn({error: err}, "HistoryViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        const additionalDataFields = [
            "executedOn",
            "completedOn",
            "executedBy",
            "status",
            "discoverySuccessCount",
            "discoveryErrorCount",
            "discoveryDuplicateCount",
            "deviceExceedLicenseCount",
            "discoveryExcludedCount",
            "deviceAddCount",
        ];
        return this._gameEditor.whenGameEditorReady().then((networkDiscoveryPlanEntity) => {
            const userSorting = this._userGridData.getSorting() || {completedOn: -1};
            const patchedSort = this._patchSorting(userSorting);
            this._entity = networkDiscoveryPlanEntity;
            this._activityFeed = new PagingActivityFeed({
                feedKey: this._gridKey + "_feed",
                userSession: this._userSession,
                entityDef: this._entity,
                columnConfig: this._columnConfig,
                additionalDataFields,
                sort: patchedSort,
                relatedEntityDef: this._networkDiscoveryExecutionEntityDef,
            });
            this._registerListeners();
            return this.whenFeedIsReady();
        });
    }

    whenFeedIsReady() {
        return this._activityFeed.whenFeedReady().catch((err) => {
            this._logger.warn({error: err}, "PagingHistoryFeed.whenFeedReady() error.");
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = NetworkDiscoveryHistoryViewComponent;

NetworkDiscoveryHistoryViewComponent.$inject = [
    "$teve.logger",
    "$teve.entities.NetworkDiscoveryExecution",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.io",
    "$teve.utils",
    "$teve.userSession",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
];

NetworkDiscoveryHistoryViewComponent.$displayDetails = {
    type: "View",
    name: "Network Discovery Execution Summary",
    icon: "devicemanager.network_discovery_history",
    shown: true,
    title: "Network Discovery Execution Summary",
    order: 2,
};
